import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import React from 'react';
import { useGenerateBulkDrafts } from '../../../../../api/mutations/reworked/use-generate-bulk-drafts';
import { replaceTimeZone, utc } from '../../../../../utils/date-time-utils';
import { useBoolean } from '../../../../shared/hooks/use-boolean';
import { LoadableComponent } from '../../../../shared/loadable-component';
import type { BulkConfigurationSettings, BulkDraftProjection, BulkDraftProjectionPostRequest, BulkDraftSubmissionFilter } from '../bulk-drafts-types';
import { BulkPreviewTableBody } from './bulk-preview-table-body';
import { BulkPreviewTableHeaders } from './bulk-preview-table-headers';
import { BulkPreviewTitleBar } from './bulk-preview-title-bar';
import { Load } from '../../../../../types';

interface BulkPreviewProps {
    bulkConfiguration: BulkConfigurationSettings;
    projections: BulkDraftProjection[];
    onSubmit: (submissionFilter: BulkDraftSubmissionFilter) => void;
    handleBack: () => void;
}

export const BulkPreview = (props: BulkPreviewProps) => {
    const { bulkConfiguration, projections, onSubmit, handleBack } = props;

    const [loading, isLoading, doneLoading] = useBoolean(false);
    const { mutateAsync: generateProjections } = useGenerateBulkDrafts();

    const createProjections = async () => {
        isLoading();

        const finalProjections: BulkDraftProjectionPostRequest[] = projections.map(projection => {
            const { orgLocation, cargoType, date, ...baseProjectionInfo} = projection;

            return {...baseProjectionInfo,
                date: utc(replaceTimeZone(date.toDate())),
                organizationLocationId: orgLocation.id,
                cargoTypeId: cargoType.id,
            }
        });

        const createdDrafts = await generateProjections({ config: finalProjections });
        // projections are guaranteed to be sorted here from createProjection in projections-utils
        const minDate = projections[0].date.toISOString();
        const maxDate = projections[projections.length - 1].date.toISOString();
        const draftIds = createdDrafts.map((draft: Load) => draft.id);
        onSubmit({ createdIds: draftIds, weekRange: [minDate, maxDate] });
        doneLoading();
    };

    return (
        <LoadableComponent isLoading={loading}>
            <>
                <Paper>
                    <BulkPreviewTitleBar
                        numberOfDrafts={projections.reduce((total, projection) => total + projection.numberOfLoads, 0)}
                        generateProjections={createProjections}
                        handleBack={handleBack}
                    />
                    <div className='bulk-entry-table'>
                        <Table>
                            <BulkPreviewTableHeaders isOriginBased={bulkConfiguration.checked} />
                            <BulkPreviewTableBody finalProjections={projections}/>
                        </Table>
                    </div>
                </Paper>
            </>
        </LoadableComponent>
    );
};
